import { FILTER_TYPE_CHECKBOX } from "../constant";
import { PHRASE_URL_PART, PREFERENCE_URL, getFetchRequest, postFetchRequest } from "../store/requests";
import {errorCallback, makeUrl, successCallbackEmpty} from "../util";
import {createListOfFilters, PREFERENCE_FILTER_GROUP_GROUPS, NO_GROUP} from "./preferenceUtil";
import {preferencesGroupsStorage} from "./preferencesGroupsStorage";
import {preferencesGroupsService} from "./preferencesGroupsService";
import {Filter, ItemsStorageService} from "../shared/filtering/itemsStorageService";
import {Preference} from "./preferenceTypes";
import defaultI18n from "../i18nService";

const findSearchValueInProperty = (property, titleFilter, i18n = defaultI18n) => {
    if(i18n.t(`preference.scheme.preferenceType.${property.preferenceType}`).toLowerCase().includes(titleFilter)) {
        return true;
    }
    if(i18n.t(`recipe.scheme.recipeType.${property.preferenceItemCode}`).toLowerCase().includes(titleFilter)) {
        return true;
    }
    if(i18n.t(`preference.scheme.when.${property.when}`).toLowerCase().includes(titleFilter)) {
        return true;
    }
    return false;
};

const parseFilterValue = (filterValue) => {
    if (filterValue === "true") return true;
    if (filterValue === "false") return false;
    return filterValue;
};

interface PreferencesStorage {
    loadItems(callback: Function): void;
    getItems(i18n?: typeof defaultI18n): Preference[];
    getSelectedPreferencesList(): Preference[];
    selectPreference(preferenceId: string, select: boolean): void;
    cleanSelectedPreferences(): void;
    generatePreferencePhrases(preferenceId: string): void;
    getPreferencePhrases(preferenceId: string, isRecreate: boolean): void;
    getPreferencePhrasesCount(preferenceId: string, callback: Function): void;
    getById(preferenceId: string): Preference;
    clearService(): void;
    getPathForPhrasesPage(preference: Preference): string;
}

class PreferencesStorageImpl implements ItemsStorageService, PreferencesStorage {
    getFilter(filterName: string): Filter {
        throw new Error("Method not implemented.");
    }
    setSelectedFilterValue(filterName: string, selectedValue: string): void {
        throw new Error("Method not implemented.");
    }
    preferencesList: Preference[] = [];

    searchValue: string = '';
    possibleFilters: Filter[] = [];
    filtersList: Filter[] = [];
    observers: Set<Function> = new Set();

    loadItems(callback: Function) {
        try {
            getFetchRequest(makeUrl([PREFERENCE_URL]), (response) => {
                this.preferencesList = [...response];
                callback && callback();
                this.notifyFiltersChangingObservers();
            }, errorCallback);
        } catch(err) {
            console.log(err);
        }
    }

    loadFilters(callback: Function, i18n = defaultI18n) {
        try {
            this.possibleFilters = [];
            this.possibleFilters.push(...createListOfFilters());
            preferencesGroupsStorage.loadItems(() => {
                this.possibleFilters.push(...preferencesGroupsStorage.getItems().map(it => {
                    return {
                        filterTitle: preferencesGroupsService.getLocalization(i18n.resolvedLanguage, it, false),
                        filterName: `${PREFERENCE_FILTER_GROUP_GROUPS}.${it.name}`,
                        filterType: FILTER_TYPE_CHECKBOX,
                        variants: [],
                        value: false,
                        filterGroupName: PREFERENCE_FILTER_GROUP_GROUPS,
                    };
                }));
                this.possibleFilters.push({
                    filterTitle: i18n.t('preference.scheme.groups.noGroup'),
                    filterName: `${PREFERENCE_FILTER_GROUP_GROUPS}.${NO_GROUP}`,
                    filterType: FILTER_TYPE_CHECKBOX,
                    variants: [],
                    value: false,
                    filterGroupName: PREFERENCE_FILTER_GROUP_GROUPS,
                });
                callback && callback();
                this.notifyFiltersChangingObservers();
            });
        } catch(err) {
            console.log(err);
        }
    }

    getItems(i18n = defaultI18n) {
        let filteredList = [...this.preferencesList];
        if(this.searchValue) {
            filteredList = this.preferencesList.filter(it => findSearchValueInProperty(it, this.searchValue, i18n));
        }
        if(this.filtersList.length) {
            this.filtersList.forEach(filter => {
                const filterValue = parseFilterValue(filter.filterName.split('.')[1]);
                filteredList = filteredList.filter(it => {
                    if (filter.filterGroupName === PREFERENCE_FILTER_GROUP_GROUPS) {
                        if (filterValue === NO_GROUP) {
                            return it.groups.length === 0;
                        }
                        return it.groups.filter(group => group.name === filterValue).length > 0;
                    } else {
                        return it[filter.filterGroupName] === filterValue;
                    }
                });
            });
        }
        return filteredList;
    }
    getSelectedList() {
        return this.preferencesList.filter(it => it.isSelected);
    }
    setInitialFilters(searchParams, callback: Function) {
        if (!this.possibleFilters.length) {
            this.loadFilters(null, () => {
                this.loadInitialFilters(searchParams, callback);
            });
        } else {
            this.loadInitialFilters(searchParams, callback);
        }

    }
    loadInitialFilters(searchParams, callback: Function) {
        const filtersList = [];
        searchParams.forEach((value, key) => {
            const currentFilter = this.possibleFilters.find(it => it.filterName === key);
            if(currentFilter && currentFilter.filterType === FILTER_TYPE_CHECKBOX && value === 'true') {
                currentFilter.value = value;
                filtersList.push(currentFilter);
            }
        });
        this.filtersList = filtersList;
        callback && callback();
    }
    getPossibleFilters() {
        return this.possibleFilters;
    }
    getCurrentFilters() {
        console.log("--------------------------getCurrentFilters");
        return this.filtersList;
    }
    getAnyFiltersByGroup(groupName: string) {
        return this.possibleFilters.find((it: Filter) => it.filterGroupName === groupName);
    }
    getCurrentFiltersByGroup(groupName) {
        return this.possibleFilters.filter(it => it.filterGroupName === groupName);
    }
    setSearchString(searchStringValue) {
        this.searchValue = searchStringValue;
        this.notifyFiltersChangingObservers();
    }
    getSearchString() {
        return this.searchValue;
    }
    setCheckedFilterValue(filterName, selectedValue) {
        const currentFilter = this.filtersList.find(it => it.filterName === filterName);
        if(currentFilter) {
            currentFilter.value = selectedValue;
        } else {
            const possibleFilter = this.possibleFilters.find(it => it.filterName === filterName);
            possibleFilter.value = selectedValue;
            this.filtersList.push(possibleFilter);
        }
        this.filtersList = this.filtersList.filter(it => 
            it.filterType !== FILTER_TYPE_CHECKBOX || (it.filterType === FILTER_TYPE_CHECKBOX && it.value === true));
    }
    setNumberFilterValue() {
    }
    cleanFilterValues() {
        this.filtersList = [];
        this.possibleFilters.forEach(it => {
            it.value = false;
        });
        this.notifyFiltersChangingObservers();
    }
    registerFiltersChangingObserver(observer) {
        this.observers.add(observer);
    }
    unRegisterFiltersChangingObserver(observer) {
        this.observers.delete(observer);
    }
    notifyFiltersChangingObservers() {
        this.observers.forEach((observer: Function) => observer());
    }
    getSelectedPreferencesList() {
        return this.preferencesList.filter(it => it.isSelected);
    }
    selectPreference(preferenceId: string, select: boolean) {
        if(preferenceId) {
            this.preferencesList.forEach(it => {
                if (it.id === preferenceId) {
                    it.isSelected = select;
                }
            });
            this.notifyFiltersChangingObservers();
        }
    }
    cleanSelectedPreferences() {}
    generatePreferencePhrases(preferenceId: string) {
        postFetchRequest(makeUrl([PREFERENCE_URL,preferenceId, PHRASE_URL_PART]), null, successCallbackEmpty, errorCallback);
    }
    getPreferencePhrases(preferenceId, isRecreate) {
        const preference = this.preferencesList.find(it => it.id === preferenceId);
        if(preference.phrase && !isRecreate) {
            return preference.phrase;
        } else {
            getFetchRequest(makeUrl([PREFERENCE_URL, preferenceId, PHRASE_URL_PART]), (response) => {
                preference.phrase = response;
                this.notifyFiltersChangingObservers();
            }, errorCallback);
            return [];
        }
    }
    getPreferencePhrasesCount(preferenceId, callback) {
        getFetchRequest(makeUrl([PREFERENCE_URL, preferenceId, PHRASE_URL_PART, '?onlyCount=true']), (response) => {
            this.preferencesList.forEach(it => it.id === preferenceId ? it.count = response.count : null);
            callback(response);
        }, errorCallback);
    }
    getById(preferenceId) {
        return this.preferencesList.find(it => it.id === preferenceId);
    }
    clearService() {
        this.preferencesList = [];
        this.notifyFiltersChangingObservers();
    }
    reloadPagesNumber() {
    }
    changeDeletedItemsView() {
    }
    changeValidItemsView() {
    }
    getPathForPhrasesPage(preference: Preference) {
        let result = '/preference/phrase/list?';
        if (preference.include) {
            result += "include.true=true&";
        } else {
            result += "include.false=true&";
        }
        result += `preferenceType.${preference.preferenceType}=true&when.${preference.when}=true`;
        return result;
    }
}

export const preferencesStorageInstance = new PreferencesStorageImpl();
